import { ChangeEvent, FC, useCallback } from 'react';
import { useSelector } from 'react-redux';

import {
  ProgressButton,
  TextArea,
  Typography,
} from '@beeline/design-system-react/extracted-styles';

import { FeedbackData, FeedbackStatus } from 'src/api/feedback';
import { EmployeeInfo } from 'src/components/EmployeeInfo/EmployeeInfo';
import { Panel } from 'src/components/Panel/Panel';
import { Section } from 'src/components/Section/Section';
import {
  getCurrentFeedback,
  getFeedbackState,
} from 'src/store/feedbacks/selectors';
import {
  makeFeedbackAccepted,
  makeFeedbackCompleted,
} from 'src/store/feedbacks/services';
import { feedbackActions } from 'src/store/feedbacks/slice';
import { sidesheetActions } from 'src/store/sidesheet/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';

interface ActionsProps {
  feedback: FeedbackData;
}

const Actions: FC<ActionsProps> = ({ feedback }) => {
  const dispatch = useAppDispatch();

  const handleComplete = useCallback(async () => {
    await dispatch(makeFeedbackCompleted(feedback.id));
    await dispatch(sidesheetActions.close());
  }, [dispatch, feedback.id]);

  const handleAccept = useCallback(async () => {
    await dispatch(makeFeedbackAccepted(feedback.id));
  }, [dispatch, feedback.id]);

  if (feedback.status === FeedbackStatus.Created)
    return (
      <ProgressButton
        variant='contained'
        onClick={handleAccept}
        fullWidth
        data-testid='acceptFeedback'
        statusDelayTime={0}
      >
        Взять в работу
      </ProgressButton>
    );

  if (feedback.status === FeedbackStatus.Accepted)
    return (
      <ProgressButton
        variant='contained'
        onClick={handleComplete}
        fullWidth
        data-testid='completeFeedback'
        statusDelayTime={0}
      >
        Завершить
      </ProgressButton>
    );

  return null;
};

interface InnerProps {
  feedback: FeedbackData;
}

const Inner: FC<InnerProps> = ({ feedback }) => {
  const dispatch = useAppDispatch();

  const { comment } = useSelector(getFeedbackState);

  const changeComment = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      dispatch(feedbackActions.setComment(e.target.value));
    },
    [dispatch],
  );

  return (
    <Panel actions={<Actions feedback={feedback} />}>
      <Section title='От кого' testId='authorSection'>
        <EmployeeInfo
          employeeNumber={feedback.createdBy.employeeNumber}
          testId='feedbackPanelAuthor'
        />
      </Section>

      <Section title='Текст обращения' testId='messageSection'>
        <Typography variant='body3' data-testid='feedbackPanelMessage'>
          {feedback.message}
        </Typography>
      </Section>

      {feedback.status === FeedbackStatus.Accepted && (
        <Section
          title='Укажите комментарий'
          testId='fillCompleteCommentSection'
        >
          <TextArea
            value={comment}
            onChange={changeComment}
            dataTestId='fillFeedbackPanelCompleteComment'
          />
        </Section>
      )}

      {feedback.status === FeedbackStatus.Completed &&
        feedback.completedComment && (
          <Section title='Комментарий' testId='completeCommentSection'>
            <Typography
              variant='body3'
              data-testid='feedbackPanelCompleteComment'
            >
              {feedback.completedComment}
            </Typography>
          </Section>
        )}
    </Panel>
  );
};

export const FeedbackSidesheetPanel: FC = () => {
  const feedback = useSelector(getCurrentFeedback);

  return feedback ? <Inner feedback={feedback} /> : null;
};
